// src/redux/slices/AlertTableSlice.js
// Fall alerts — store key: state.fallAlerts
// Action prefix changed from "alerts/" → "fallAlerts/" to prevent
// collision with AlertSlice which also used "alerts/" prefix.

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const getAuthToken = (getState) =>
  (getState && getState().auth && getState().auth.token) ||
  sessionStorage.getItem("access_token") ||
  localStorage.getItem("access_token");

// ── Fetch fall alerts for a specific user (nurse) ──
export const fetchFallAlertsByUser = createAsyncThunk(
  "fallAlerts/fetchByUser",
  async (userId, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      const response = await axios.get(
        `${API_URL}/alerts_table/user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

// ── Fetch ALL fall alerts (admin) ──
export const fetchAllFallAlerts = createAsyncThunk(
  "fallAlerts/fetchAll",
  async (acknowledged = null, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      let url = `${API_URL}/alerts_table/`;
      if (acknowledged !== null) url += `?acknowledged=${acknowledged}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

// ── Fetch single fall alert ──
export const fetchFallAlertById = createAsyncThunk(
  "fallAlerts/fetchById",
  async (alertId, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      const response = await axios.get(
        `${API_URL}/alerts_table/${alertId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

// ── Fetch fall alerts by patient_id ──
export const fetchFallAlertsByPatient = createAsyncThunk(
  "fallAlerts/fetchByPatient",
  async (patientId, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      const response = await axios.get(
        `${API_URL}/alerts_table/patient/${patientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

// ── Acknowledge fall alert with note ──
export const acknowledgeFallAlertById = createAsyncThunk(
  "fallAlerts/acknowledgeById",
  async (
    { alertId, alert_acknowledged, note, user_id },
    { rejectWithValue, getState }
  ) => {
    try {
      const token = getAuthToken(getState);
      const response = await axios.patch(
        `${API_URL}/alerts_table/${alertId}/acknowledge`,
        { alert_acknowledged, note, user_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

// ── Create fall alert (Jetson Nano / device) ──
export const createFallAlert = createAsyncThunk(
  "fallAlerts/create",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      const response = await axios.post(
        `${API_URL}/alerts_table/create`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const alertTableSlice = createSlice({
  name: "fallAlerts",
  initialState: {
    alerts: [],
    selectedAlert: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearSelectedFallAlert(state) {
      state.selectedAlert = null;
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state) => {
      state.loading = true;
      state.error = null;
    };
    const setError = (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error.message;
    };

    builder
      // fetchFallAlertsByUser
      .addCase(fetchFallAlertsByUser.pending, setLoading)
      .addCase(fetchFallAlertsByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload;
      })
      .addCase(fetchFallAlertsByUser.rejected, setError)

      // fetchAllFallAlerts
      .addCase(fetchAllFallAlerts.pending, setLoading)
      .addCase(fetchAllFallAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload;
      })
      .addCase(fetchAllFallAlerts.rejected, setError)

      // fetchFallAlertsByPatient
      .addCase(fetchFallAlertsByPatient.pending, setLoading)
      .addCase(fetchFallAlertsByPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload;
      })
      .addCase(fetchFallAlertsByPatient.rejected, setError)

      // fetchFallAlertById
      .addCase(fetchFallAlertById.fulfilled, (state, action) => {
        state.selectedAlert = action.payload;
      })

      // acknowledgeFallAlertById
      .addCase(acknowledgeFallAlertById.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.alerts.findIndex(
          (a) => a.alert_id === updated.alert_id
        );
        if (idx !== -1) state.alerts[idx] = updated;
      })

      // createFallAlert
      .addCase(createFallAlert.fulfilled, (state, action) => {
        state.alerts.unshift(action.payload);
      });
  },
});

export const { clearSelectedFallAlert } = alertTableSlice.actions;
export default alertTableSlice.reducer;